<script lang="ts">
import type { CalendarRootEmits, CalendarRootProps, DateValue } from 'reka-ui'
import type { HTMLAttributes, Ref } from 'vue'

export type CalendarProps = CalendarRootProps & {
  class?: HTMLAttributes['class']
}

export type CalendarEmits = CalendarRootEmits
</script>

<script setup lang="ts">
import { reactiveOmit, useVModel } from '@vueuse/core'
import { getLocalTimeZone, today } from '@internationalized/date'
import { format } from 'date-fns'
import {
  CalendarCell,
  CalendarCellTrigger,
  CalendarGrid,
  CalendarGridBody,
  CalendarGridHead,
  CalendarGridRow,
  CalendarHeadCell,
  CalendarHeader,
  CalendarNext,
  CalendarPrev,
  CalendarRoot,
  useForwardProps,
} from 'reka-ui'
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/vue/24/outline'
import { ref } from 'vue'
import { cn } from '#ui-kit/lib/utils'
import CalendarMonthGrid from './CalendarMonthGrid.vue'

const props = withDefaults(defineProps<CalendarProps>(), {
  weekdayFormat: 'short',
  weekStartsOn: 1,
})
const emits = defineEmits<CalendarEmits>()

// `placeholder` is owned here (passive v-model) so the month picker can drive it;
// the rest of the props/emits are forwarded straight to CalendarRoot.
const delegated = reactiveOmit(props, 'class', 'placeholder')
const forwarded = useForwardProps(delegated)

const placeholder = useVModel(props, 'placeholder', emits, {
  passive: true,
  defaultValue: props.defaultPlaceholder ?? today(getLocalTimeZone()),
}) as Ref<DateValue>

const view = ref<'days' | 'months'>('days')

function formatMonth(value: { year: number; month: number }): string {
  return format(new Date(value.year, value.month - 1, 1), 'MMMM yyyy')
}

function onMonthSelect(value: DateValue): void {
  placeholder.value = value
  view.value = 'days'
}
</script>

<template>
  <CalendarRoot
    v-slot="{ weekDays, grid }"
    v-bind="forwarded"
    v-model:placeholder="placeholder"
    :class="cn(props.class)"
    @update:model-value="(value) => emits('update:modelValue', value)"
  >
    <CalendarMonthGrid
      v-if="view === 'months'"
      :placeholder="placeholder"
      :min-value="props.minValue"
      :max-value="props.maxValue"
      @update:placeholder="(value) => (placeholder = value)"
      @select="onMonthSelect"
    />

    <template v-else>
      <CalendarHeader class="relative mb-2 flex w-full items-center justify-between border-b border-neutral-400 pb-2">
        <CalendarPrev
          class="inline-flex h-7 w-7 items-center justify-center rounded-md text-accent transition-colors hover:bg-neutral-300 focus:outline-none disabled:pointer-events-none disabled:opacity-50"
        >
          <ChevronLeftIcon class="h-4 w-4" />
        </CalendarPrev>
        <button
          v-for="month in grid"
          :key="`heading-${month.value.toString()}`"
          type="button"
          class="flex-1 text-center text-sm font-medium text-accent hover:underline"
          @click="view = 'months'"
        >
          {{ formatMonth(month.value) }}
        </button>
        <CalendarNext
          class="inline-flex h-7 w-7 items-center justify-center rounded-md text-accent transition-colors hover:bg-neutral-300 focus:outline-none disabled:pointer-events-none disabled:opacity-50"
        >
          <ChevronRightIcon class="h-4 w-4" />
        </CalendarNext>
      </CalendarHeader>

      <div v-for="month in grid" :key="month.value.toString()">
        <CalendarGrid class="w-full border-collapse select-none">
          <CalendarGridHead>
            <CalendarGridRow class="flex w-full">
              <CalendarHeadCell v-for="day in weekDays" :key="day" class="w-9 text-xs font-semibold text-secondary">
                {{ day }}
              </CalendarHeadCell>
            </CalendarGridRow>
          </CalendarGridHead>
          <CalendarGridBody>
            <CalendarGridRow v-for="(weekDates, index) in month.rows" :key="`weekDate-${index}`" class="flex w-full">
              <CalendarCell
                v-for="weekDate in weekDates"
                :key="weekDate.toString()"
                :date="weekDate"
                class="relative h-9 w-9 p-0 text-center text-sm"
              >
                <CalendarCellTrigger
                  :day="weekDate"
                  :month="month.value"
                  :class="
                    cn(
                      'inline-flex h-9 w-9 items-center justify-center rounded-full p-0 text-sm text-primal transition-colors',
                      'hover:bg-neutral-300 focus:outline-none',
                      'data-today:not-data-selected:text-accent',
                      'data-selected:bg-accent data-selected:text-white data-selected:hover:bg-accent',
                      'data-disabled:pointer-events-none data-disabled:text-disabled/30',
                      'data-unavailable:pointer-events-none data-unavailable:text-disabled/30 data-unavailable:line-through',
                      'data-outside-view:invisible',
                    )
                  "
                />
              </CalendarCell>
            </CalendarGridRow>
          </CalendarGridBody>
        </CalendarGrid>
      </div>
    </template>
  </CalendarRoot>
</template>
